import type { Answers, ItemStates } from "@features/session/hooks/types";
import type { BaseItemSchema } from "@memorang/types";
import type {
	SessionItemsNavFilterValue,
	SessionItemsNavItem,
	SessionItemsNavItemStatus,
} from "@memorang/ui/components/SessionItemsNavPane";

type BuildSessionItemsNavItemsParams = {
	items: BaseItemSchema[];
	itemStates?: ItemStates;
	answers?: Answers;
	markedItemIds?: number[];
	inReviewMode?: boolean;
};

const getItemLabel = (index: number) => {
	return String(index + 1);
};

const getSelectedChoiceIds = (
	item: BaseItemSchema,
	itemStates?: ItemStates,
	answers?: Answers,
) => {
	const itemId = item.id;
	if (itemId == null) {
		return [];
	}

	return (
		itemStates?.get(itemId)?.selectedChoiceIds ||
		answers?.get(itemId)?.selectedChoiceIds ||
		item.answer?.selectedChoiceIds ||
		[]
	);
};

const getIsCorrect = (
	item: BaseItemSchema,
	itemStates?: ItemStates,
	answers?: Answers,
) => {
	const itemId = item.id;
	if (itemId == null) {
		return false;
	}

	const persistedCorrect =
		answers?.get(itemId)?.correct ?? item.answer?.correct ?? null;
	if (persistedCorrect != null) {
		return persistedCorrect;
	}

	const selectedChoiceIds = getSelectedChoiceIds(item, itemStates, answers);
	const response = item.response;
	if (response.type !== "choice" || !selectedChoiceIds.length) {
		return false;
	}

	if (response.selectionType === "multiple") {
		const correctChoiceIds = response.choices
			.filter((choice) => choice.isCorrect)
			.map((choice) => choice.id);

		return (
			selectedChoiceIds.length === correctChoiceIds.length &&
			selectedChoiceIds.every((choiceId) => correctChoiceIds.includes(choiceId))
		);
	}

	const correctChoiceId = response.choices.find(
		(choice) => choice.isCorrect,
	)?.id;
	return selectedChoiceIds[0] === correctChoiceId;
};

const getHasResponse = (
	item: BaseItemSchema,
	itemStates?: ItemStates,
	answers?: Answers,
) => {
	const itemId = item.id;
	if (itemId == null) {
		return false;
	}

	const selectedChoiceIds = getSelectedChoiceIds(item, itemStates, answers);

	return (
		selectedChoiceIds.length > 0 ||
		Boolean(itemStates?.get(itemId)?.answered) ||
		Boolean(answers?.get(itemId)) ||
		Boolean(item.answer)
	);
};

const getItemStatus = ({
	item,
	itemStates,
	answers,
	inReviewMode,
}: {
	item: BaseItemSchema;
	itemStates?: ItemStates;
	answers?: Answers;
	inReviewMode?: boolean;
}): SessionItemsNavItemStatus => {
	const hasResponse = getHasResponse(item, itemStates, answers);

	if (!hasResponse) {
		return inReviewMode ? "skipped" : "unanswered";
	}

	if (!inReviewMode) {
		return "answered";
	}

	const selectedChoiceIds = getSelectedChoiceIds(item, itemStates, answers);
	if (selectedChoiceIds.length === 0) {
		return "skipped";
	}

	return getIsCorrect(item, itemStates, answers) ? "correct" : "incorrect";
};

export const getSessionItemsNavFiltersForMode = (
	inReviewMode?: boolean,
): SessionItemsNavFilterValue[] =>
	inReviewMode
		? ["correct", "incorrect", "skipped", "marked"]
		: ["answered", "unanswered", "marked"];

export const buildSessionItemsNavItems = ({
	items,
	itemStates,
	answers,
	markedItemIds,
	inReviewMode,
}: BuildSessionItemsNavItemsParams): SessionItemsNavItem[] => {
	return items.map((item, index) => ({
		id: String(item.id),
		label: getItemLabel(index),
		status: getItemStatus({
			item,
			itemStates,
			answers,
			inReviewMode,
		}),
		isMarked:
			Boolean(item.answer?.marked) ||
			Boolean(markedItemIds?.includes(item.id ?? -1)),
	}));
};

export const getSessionItemsNavProgress = (
	targetIndex: number,
	totalItems: number,
) => {
	if (totalItems <= 0) {
		return 0;
	}

	return (targetIndex + 1) / totalItems;
};
